import React, { useMemo, useCallback, useState } from "react";
import { Row, Col, Form, Button, Card, Alert, InputGroup, Badge } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { FaPlus, FaTrash, FaExclamationTriangle, FaCheckCircle } from "react-icons/fa";

const SHIPMENT_TYPES = {
  Palette: { weight: 100, length: 120, width: 80, height: 100 },
  Paket: { weight: 10, length: 40, width: 40, height: 40 },
  Dokument: { weight: 0, length: 0, width: 0, height: 0 },
  Fhrrader: { weight: 15, length: 150, width: 40, height: 80 },
  "Komplettes Fahrzeug": null,
  Sonstige: { weight: 50, length: 60, width: 60, height: 60 }
};

// ✅ معيار القيم المسموح بها للباك إند
const SHIPMENT_TYPE_MAP = {
  Palette: "pallete",
  Paket: "pakete",
  Dokument: "dokument",
  Fhrrader: "fahrrader",
  "Komplettes Fahrzeug": "komplettes fahrzeug",
  Sonstige: "other"
};

export default function StepOne({
  shipments,
  setShipments,
  validationErrors,
  selectedVehicleInfo,
  extraServices,
  onExtraServiceChange,
  message,
  setMessage,
  pickupForms,
}) {
  const { t } = useTranslation();
  
  // ✅ حالة لتخزين النص المخصص لـ Sonstige
  const [customShipmentText, setCustomShipmentText] = useState({});

  const shipmentTypes = useMemo(() => ({
    ...SHIPMENT_TYPES,
    "Komplettes Fahrzeug": {
      weight: selectedVehicleInfo?.weight || 1000,
      length: selectedVehicleInfo?.length || 400,
      width: selectedVehicleInfo?.width || 200,
      height: selectedVehicleInfo?.height || 200
    }
  }), [selectedVehicleInfo]);

  const hasCompleteVehicle = useMemo(() =>
    shipments.some(s => s?.type === "Komplettes Fahrzeug"),
    [shipments]
  );

  const calculateTotals = useCallback(() => {
    return shipments.reduce((acc, shipment) => {
      const quantity = Number(shipment?.quantity) || 0;
      let weight = Number(shipment?.weight) || 0;

      if (shipment?.type === "Dokument") {
        weight = 0;
      }

      const length = Number(shipment?.length) || 0;
      const width = Number(shipment?.width) || 0;
      const height = Number(shipment?.height) || 0;
      const totalPalletQuantity = shipments
        .filter(s => s.type === "Palette")
        .reduce((sum, s) => sum + (Number(s.quantity) || 0), 0);

      return {
        totalWeight: acc.totalWeight + (quantity * weight),
        maxLength: Math.max(acc.maxLength, length),
        maxWidth: Math.max(acc.maxWidth, width),
        maxHeight: Math.max(acc.maxHeight, height),
        totalVolume: acc.totalVolume + (quantity * length * width * height),
        totalPalletQuantity
      };
    }, { totalWeight: 0, maxLength: 0, maxWidth: 0, maxHeight: 0, totalVolume: 0, totalPalletQuantity: 0 });
  }, [shipments]);

  const totals = useMemo(() => calculateTotals(), [calculateTotals]);

  const checkVehicleLimits = useCallback(() => {
    const vehicleMaxWeight = selectedVehicleInfo?.weight || 800;
    const vehicleLength = selectedVehicleInfo?.length || 420;
    const vehicleWidth = selectedVehicleInfo?.width || 210;
    const vehicleHeight = selectedVehicleInfo?.height || 210;
    const vehicleAmount = selectedVehicleInfo?.amount || 2;

    const errors = [];

    if (totals.totalPalletQuantity > vehicleAmount) {
      errors.push(t("pallet_quantity_exceeds_vehicle_limit", { totalPalletQuantity: totals.totalPalletQuantity, maxPallets: vehicleAmount }));
    }

    if (totals.totalWeight > vehicleMaxWeight) {
      errors.push(t("weight_exceeds_vehicle_limit", { totalWeight: totals.totalWeight, maxWeight: vehicleMaxWeight }));
    }

    if (totals.maxLength > vehicleLength) {
      errors.push(t("length_exceeds_vehicle_limit", { maxLength: totals.maxLength, vehicleLength }));
    }

    if (totals.maxWidth > vehicleWidth) {
      errors.push(t("width_exceeds_vehicle_limit", { maxWidth: totals.maxWidth, vehicleWidth }));
    }

    if (totals.maxHeight > vehicleHeight) {
      errors.push(t("height_exceeds_vehicle_limit", { maxHeight: totals.maxHeight, vehicleHeight }));
    }

    return errors;
  }, [totals, selectedVehicleInfo, t]);

  const vehicleLimitErrors = useMemo(() => checkVehicleLimits(), [checkVehicleLimits]);

  const handleChange = useCallback((index, field, value) => {
    const newShipments = [...shipments];
    newShipments[index] = { ...newShipments[index], [field]: value };

    if (newShipments[index].type === "Dokument") {
      newShipments[index].weight = 0;
      newShipments[index].length = 0;
      newShipments[index].width = 0;
      newShipments[index].height = 0;
    }

    setShipments(newShipments);
  }, [shipments, setShipments]);

  const handleTypeChange = useCallback((index, newType) => {
    const newShipments = [...shipments];

    if (newType === "Komplettes Fahrzeug") {
      newShipments[0] = {
        quantity: 1,
        type: "Komplettes Fahrzeug",
        weight: shipmentTypes["Komplettes Fahrzeug"].weight,
        length: shipmentTypes["Komplettes Fahrzeug"].length,
        width: shipmentTypes["Komplettes Fahrzeug"].width,
        height: shipmentTypes["Komplettes Fahrzeug"].height,
        notes: "",
        customShipmentText: "", // ✅ إضافة حقل النص المخصص
      };
      setShipments([newShipments[0]]);
      return;
    }

    const typeData = shipmentTypes[newType] || {};

    newShipments[index] = {
      ...newShipments[index],
      type: newType,
      weight: typeData.weight ?? newShipments[index].weight ?? 0,
      length: typeData.length ?? newShipments[index].length ?? 0,
      width: typeData.width ?? newShipments[index].width ?? 0,
      height: typeData.height ?? newShipments[index].height ?? 0,
      // ✅ إذا كان النوع Sonstige، نضيف حقل النص المخصص
      customShipmentText: newType === "Sonstige" ? (newShipments[index]?.customShipmentText || "") : "",
    };

    setShipments(newShipments);
  }, [shipments, shipmentTypes, setShipments]);

  // ✅ دالة لتحديث النص المخصص لـ Sonstige
  const handleCustomShipmentChange = useCallback((index, value) => {
    const newShipments = [...shipments];
    newShipments[index] = {
      ...newShipments[index],
      customShipmentText: value,
      // ✅ نحتفظ بالنوع Sonstige في حقل type
      type: "Sonstige",
    };
    setShipments(newShipments);
  }, [shipments, setShipments]);

  const addShipment = useCallback(() => {
    if (hasCompleteVehicle || (selectedVehicleInfo?.max_shipments && shipments.length >= selectedVehicleInfo.max_shipments)) {
      return;
    }

    const newShipments = [...shipments];
    newShipments.push({
      quantity: 1,
      type: "Palette",
      weight: shipmentTypes.Palette.weight,
      length: shipmentTypes.Palette.length,
      width: shipmentTypes.Palette.width,
      height: shipmentTypes.Palette.height,
      notes: "",
      customShipmentText: "",
    });
    setShipments(newShipments);
  }, [hasCompleteVehicle, selectedVehicleInfo, shipments, shipmentTypes, setShipments]);

  const removeShipment = useCallback((index) => {
    if (shipments.length > 1) {
      const newShipments = shipments.filter((_, i) => i !== index);
      setShipments(newShipments);
    } else {
      const newShipments = [...shipments];
      newShipments[index] = {
        quantity: 1,
        type: "Palette",
        weight: shipmentTypes.Palette.weight,
        length: shipmentTypes.Palette.length,
        width: shipmentTypes.Palette.width,
        height: shipmentTypes.Palette.height,
        notes: "",
        customShipmentText: "",
      };
      setShipments(newShipments);
    }
  }, [shipments, setShipments, shipmentTypes]);

  const shouldShowDimensions = useCallback((shipmentType) => {
    return shipmentType !== "Dokument";
  }, []);

  const isFieldEditable = useCallback((shipmentType, fieldName) => {
    if (shipmentType === "Komplettes Fahrzeug") {
      return fieldName === "type";
    }
    return true;
  }, []);

  const renderVehicleInfo = () => (
    <Card className="mb-4 border-0" style={{ borderRadius: "16px", backgroundColor: "#ffffff", border: "1px solid #e2e8f0" }}>
      <Card.Body className="p-3">
        <div className="d-flex justify-content-between align-items-center">
          <h6 className="mb-0 text-dark fw-bold d-flex align-items-center">
            <FaCheckCircle className="me-2 text-dark" />
            {t("vehicle_info")}
          </h6>
          <Badge bg="primary" className="px-3 py-2 text-uppercase fw-bold" style={{ backgroundColor: "#2563eb", borderRadius: "6px" }}>
            {selectedVehicleInfo?.name || t("standard_vehicle")}
          </Badge>
        </div>
        <Row className="mt-3">
          <Col sm={6}>
            <small className="text-muted d-block mb-1">{t("max_weight")}:</small>
            <div className="fw-bold fs-5" style={{ color: "#ff6b00" }}>{selectedVehicleInfo?.weight || 800} kg</div>
          </Col>
          <Col sm={6}>
            <small className="text-muted d-block mb-1">{t("dimensions_label")}:</small>
            <div className="fw-bold fs-5" style={{ color: "#ff6b00" }}>
              {selectedVehicleInfo?.length || 420} × {selectedVehicleInfo?.width || 210} × {selectedVehicleInfo?.height || 210} cm
            </div>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );

  return (
    <div className="step-one-container">
      {renderVehicleInfo()}

      {validationErrors.shipments && (
        <Alert variant="danger" className="mb-3">
          <FaExclamationTriangle className="me-2" />
          {validationErrors.shipments}
        </Alert>
      )}

      {vehicleLimitErrors.length > 0 && (
        <Alert variant="danger" className="mb-3">
          <ul className="mb-0 mt-2">
            {vehicleLimitErrors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </Alert>
      )}

      <div className="shipments-section mb-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0 text-dark fw-bold">{t("shipments")}</h5>
          {!hasCompleteVehicle && (
            <Button
              variant="outline-warning"
              size="sm"
              className="fw-bold px-3 py-1 d-flex align-items-center"
              style={{ borderColor: "#ff6b00", color: "#ff6b00", borderRadius: "8px", backgroundColor: "#ffffff" }}
              onClick={addShipment}
              disabled={selectedVehicleInfo?.max_shipments && shipments.length >= selectedVehicleInfo.max_shipments}
            >
              <FaPlus className="me-1" /> {t("add_shipment")}
            </Button>
          )}
        </div>

        {shipments.map((shipment, shipmentIndex) => (
          <Card key={shipmentIndex} className="mb-3 shipment-card border-0" style={{ borderRadius: "16px", backgroundColor: "#ffffff", border: "1px solid #e2e8f0" }}>
            <Card.Body className="p-3">
              <div className="d-flex justify-content-between align-items-start mb-3">
                <div className="d-flex align-items-center">
                  <h6 className="mb-0 me-2 text-dark fw-bold">
                    {t("item")} {shipmentIndex + 1}
                  </h6>
                  {shipment?.type === "Komplettes Fahrzeug" && (
                    <Badge bg="warning" text="dark">
                      {t("complete_vehicle")}
                    </Badge>
                  )}
                </div>
                <Button
                  variant="outline-danger"
                  size="sm"
                  className="p-1 px-2"
                  style={{ backgroundColor: "#fef2f2", borderColor: "#fee2e2", color: "#ef4444", borderRadius: "8px" }}
                  onClick={() => removeShipment(shipmentIndex)}
                  disabled={shipment?.type === "Komplettes Fahrzeug" && shipments.length === 1}
                >
                  <FaTrash />
                </Button>
              </div>

              <Row className="g-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-bold">{t("shipment_type")} *</Form.Label>
                    <Form.Select
                      value={shipment?.type || "Palette"}
                      onChange={(e) => handleTypeChange(shipmentIndex, e.target.value)}
                      isInvalid={!!validationErrors[`type_${shipmentIndex}`]}
                      data-error={`type_${shipmentIndex}`}
                      disabled={hasCompleteVehicle && shipment?.type !== "Komplettes Fahrzeug"}
                    >
                      <option value="Palette">{t("palette")}</option>
                      <option value="Paket">{t("paket")}</option>
                      <option value="Dokument">{t("dokument")}</option>
                      <option value="Komplettes Fahrzeug">{t("complete_vehicle")}</option>
                      <option value="Sonstige">{t("sonstige")}</option>
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {validationErrors[`type_${shipmentIndex}`]}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-bold">{t("quantity")} *</Form.Label>
                    <Form.Control
                      type="number"
                      min="1"
                      value={shipment?.quantity ?? ""}
                      onChange={(e) => handleChange(shipmentIndex, "quantity", e.target.value === "" ? "" : parseInt(e.target.value))}
                      isInvalid={!!validationErrors[`quantity_${shipmentIndex}`]}
                      data-error={`quantity_${shipmentIndex}`}
                      disabled={!isFieldEditable(shipment?.type, "quantity")}
                    />
                    <Form.Control.Feedback type="invalid">
                      {validationErrors[`quantity_${shipmentIndex}`]}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              {/* ✅ حقل إضافي لـ Sonstige */}
              {shipment?.type === "Sonstige" && (
                <Row className="g-3 mt-2">
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label className="fw-bold">
                        {t("shipment_description") || "Beschreibung des Gegenstands"} *
                      </Form.Label>
                      <Form.Control
                        type="text"
                        value={shipment?.customShipmentText || ""}
                        onChange={(e) => handleCustomShipmentChange(shipmentIndex, e.target.value)}
                        placeholder={t("enter_shipment_description") || "z.B. Möbelstück, Elektrogerät, etc."}
                        isInvalid={!!validationErrors[`customShipmentText_${shipmentIndex}`]}
                        data-error={`customShipmentText_${shipmentIndex}`}
                      />
                      <Form.Control.Feedback type="invalid">
                        {validationErrors[`customShipmentText_${shipmentIndex}`] || t("please_enter_description")}
                      </Form.Control.Feedback>
                      <Form.Text className="text-muted">
                        {t("sonstige_description_hint") || "Bitte geben Sie an, was transportiert werden soll."}
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
              )}

              {shouldShowDimensions(shipment?.type) && (
                <Row className="g-3 mt-2">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-bold">{t("weight_kg")} *</Form.Label>
                      <InputGroup>
                        <Form.Control
                          type="number"
                          min="0.1"
                          step="0.1"
                          value={shipment?.weight ?? ""}
                          onChange={(e) => handleChange(shipmentIndex, "weight", e.target.value === "" ? "" : parseFloat(e.target.value))}
                          isInvalid={!!validationErrors[`weight_${shipmentIndex}`]}
                          data-error={`weight_${shipmentIndex}`}
                          disabled={!isFieldEditable(shipment?.type, "weight")}
                        />
                        <InputGroup.Text>kg</InputGroup.Text>
                      </InputGroup>
                      <Form.Control.Feedback type="invalid">
                        {validationErrors[`weight_${shipmentIndex}`]}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-bold">{t("length_cm")} *</Form.Label>
                      <InputGroup>
                        <Form.Control
                          type="number"
                          min="1"
                          value={shipment?.length ?? ""}
                          onChange={(e) => handleChange(shipmentIndex, "length", e.target.value === "" ? "" : parseInt(e.target.value))}
                          isInvalid={!!validationErrors[`length_${shipmentIndex}`]}
                          data-error={`length_${shipmentIndex}`}
                          disabled={!isFieldEditable(shipment?.type, "length")}
                        />
                        <InputGroup.Text>cm</InputGroup.Text>
                      </InputGroup>
                      <Form.Control.Feedback type="invalid">
                        {validationErrors[`length_${shipmentIndex}`]}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-bold">{t("width_cm")} *</Form.Label>
                      <InputGroup>
                        <Form.Control
                          type="number"
                          min="1"
                          value={shipment?.width ?? ""}
                          onChange={(e) => handleChange(shipmentIndex, "width", e.target.value === "" ? "" : parseInt(e.target.value))}
                          isInvalid={!!validationErrors[`width_${shipmentIndex}`]}
                          data-error={`width_${shipmentIndex}`}
                          disabled={!isFieldEditable(shipment?.type, "width")}
                        />
                        <InputGroup.Text>cm</InputGroup.Text>
                      </InputGroup>
                      <Form.Control.Feedback type="invalid">
                        {validationErrors[`width_${shipmentIndex}`]}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-bold">{t("height_cm")} *</Form.Label>
                      <InputGroup>
                        <Form.Control
                          type="number"
                          min="1"
                          value={shipment?.height ?? ""}
                          onChange={(e) => handleChange(shipmentIndex, "height", e.target.value === "" ? "" : parseInt(e.target.value))}
                          isInvalid={!!validationErrors[`height_${shipmentIndex}`]}
                          data-error={`height_${shipmentIndex}`}
                          disabled={!isFieldEditable(shipment?.type, "height")}
                        />
                        <InputGroup.Text>cm</InputGroup.Text>
                      </InputGroup>
                      <Form.Control.Feedback type="invalid">
                        {validationErrors[`height_${shipmentIndex}`]}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>
              )}
            </Card.Body>
          </Card>
        ))}

        {shipments.length === 0 && (
          <Alert variant="info">
            <FaExclamationTriangle className="me-2" />
            {t("no_shipments_added")}
          </Alert>
        )}
      </div>

      <Card className="mb-4">
        <Card.Body>
          <Form.Group>
            <Form.Label className="fw-bold">{t("message_to_driver")}</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder={t("message_placeholder")}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
            <Form.Text className="text-muted">{t("message_info")}</Form.Text>
          </Form.Group>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h6 className="mb-3">{t("extra_services")}</h6>
          <Row>
            <Col md={6}>
              <Form.Check
                type="checkbox"
                id="loading-service"
                label={
                  <div>
                    <strong>{t("loading_service")}</strong>
                    <div className="text-muted small">{t("loading_service_help", { price: 25 })}</div>
                  </div>
                }
                checked={extraServices.loading}
                onChange={(e) => onExtraServiceChange('loading', e.target.checked)}
              />
            </Col>
            <Col md={6}>
              <Form.Check
                type="checkbox"
                id="unloading-service"
                label={
                  <div>
                    <strong>{t("unloading_service")}</strong>
                    <div className="text-muted small">{t("unloading_service_help", { price: 25 })}</div>
                  </div>
                }
                checked={extraServices.unloading}
                onChange={(e) => onExtraServiceChange('unloading', e.target.checked)}
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </div>
  );
}